import TeacherPageHeader from '../../components/TeacherPageHeader';
import React, { useState, useEffect, useCallback } from 'react';
import { practiceAPI, holidayAPI } from '../../services/index';
import { format, isSameDay, addMonths, subMonths } from 'date-fns';
import { ko } from 'date-fns/locale';
import SimpleCalendar from '../../components/SimpleCalendar';
import { CalendarEvent, Holiday } from '../../types/api';

const PracticeBooking: React.FC = () => {
  const [practiceSessions, setPracticeSessions] = useState<any[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedDateEvents, setSelectedDateEvents] = useState<CalendarEvent[]>([]);

  // 휴일 관련 상태
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // 휴일 데이터 로드
  const fetchHolidays = useCallback(async (targetDate: Date) => {
    try {
      const response = await holidayAPI.getHolidays({
        year: targetDate.getFullYear(),
        month: targetDate.getMonth() + 1
      });
      if (response.data?.success && response.data?.data) {
        setHolidays(response.data.data);
      }
    } catch {
      // 휴일 로드 실패
    }
  }, []);

  useEffect(() => {
    loadPracticeSessions();
    setSelectedDate(new Date());
    fetchHolidays(new Date());
  }, []);

  // 휴일 로드 useEffect
  useEffect(() => {
    fetchHolidays(currentMonth);
  }, [currentMonth, fetchHolidays]);

  useEffect(() => {
    convertSessionsToEvents();
  }, [practiceSessions]);

  useEffect(() => {
    updateSelectedDateEvents(selectedDate);
  }, [calendarEvents]);

  const loadPracticeSessions = async (targetDate?: Date) => {
    try {
      const date = targetDate || new Date();
      const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
      const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);

      const response = await practiceAPI.getAllPracticeSessions({
        startDate: format(startOfMonth, 'yyyy-MM-dd'),
        endDate: format(endOfMonth, 'yyyy-MM-dd'),
      });

      if (response.success && response.data && 'sessions' in response.data) {
        setPracticeSessions(response.data.sessions || []);
      } else {
        setPracticeSessions([]);
      }
    } catch {
      setPracticeSessions([]);
    }
  };

  const convertSessionsToEvents = () => {
    const events: CalendarEvent[] = practiceSessions
      .filter(session => session.status !== 'cancelled')
      .map(session => ({
        id: session.sessionId || session.bookingId,
        title: session.user?.name || '사용자',
        type: 'practice',
        start: new Date(session.startTime),
        end: new Date(session.endTime),
        color: session.status === 'scheduled' ? '#1e56d6' : '#0f9d76',
        data: session,
      }));
    setCalendarEvents(events);
  };

  const updateSelectedDateEvents = (date: Date) => {
    const events = calendarEvents
      .filter(event => isSameDay(new Date(event.start), date))
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    setSelectedDateEvents(events);
  };

  const handleDateClick = (date: Date, dayEvents: CalendarEvent[]) => {
    setSelectedDate(date);
    setSelectedDateEvents(dayEvents.sort((a, b) =>
      new Date(a.start).getTime() - new Date(b.start).getTime()
    ));
  };

  const handleMonthChange = (date: Date) => {
    setCurrentMonth(date);
    loadPracticeSessions(date);
  };

  const getRoomName = (roomId: string) => {
    const roomMap: Record<string, string> = {
      'room1': '연습실 1',
      'room2': '연습실 2',
      'room3': '연습실 3',
    };
    return roomMap[roomId] || roomId;
  };

  const dateLabel = format(selectedDate, 'M월 d일 (E)', { locale: ko });

  return (
    <div className="teacher-page">
      {/* 수업일정관리와 동일한 헤더 (타이틀 + 월 이동 + 범례) */}
      <TeacherPageHeader title="연습실 예약관리">
        <div className="month-nav">
          <button
            type="button"
            className="month-nav-btn"
            aria-label="이전 달"
            onClick={() => handleMonthChange(subMonths(currentMonth, 1))}
          >
            ‹
          </button>
          <span className="month-label">{format(currentMonth, 'yyyy년 M월', { locale: ko })}</span>
          <button
            type="button"
            className="month-nav-btn"
            aria-label="다음 달"
            onClick={() => handleMonthChange(addMonths(currentMonth, 1))}
          >
            ›
          </button>
          <button type="button" className="today-btn" onClick={() => handleMonthChange(new Date())}>
            오늘
          </button>
        </div>
        <div className="calendar-legend">
          <span className="legend-item"><span className="legend-swatch" style={{ background: '#1e56d6' }} />예약</span>
          <span className="legend-item"><span className="legend-swatch" style={{ background: '#0f9d76' }} />이용 완료</span>
        </div>
      </TeacherPageHeader>
      <div className="schedule-container">
        <div className="calendar-section">
          <SimpleCalendar
            hideHeader
            date={currentMonth}
            events={calendarEvents}
            holidays={holidays}
            onDateClick={handleDateClick}
            onMonthChange={handleMonthChange}
            selectedDate={selectedDate}
          />
        </div>

        <div className="schedule-sidebar">
          <div className="today-schedule-section">
            <div className="section-header">
              <h3>{dateLabel}</h3>
            </div>
            {selectedDateEvents.length > 0 ? (
              <div className="schedule-items">
                {selectedDateEvents.map((event, index) => (
                  <div
                    key={index}
                    className="schedule-item"
                    style={{ borderLeftColor: event.color }}
                  >
                    <div className="item-time">
                      {format(new Date(event.start), 'HH:mm')}
                    </div>
                    <div className="item-content">
                      <div className="item-title">{event.title}</div>
                      <div className="item-duration">
                        {format(new Date(event.start), 'HH:mm')} - {format(new Date(event.end), 'HH:mm')}
                        {(event.data as any)?.practiceRoom && ` · ${getRoomName((event.data as any).practiceRoom)}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-schedule">
                <span>예약 없음</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PracticeBooking;
